import { times } from 'lodash';
import { initClient } from '@ts-rest/core';
import { apiContract } from '@retro-ui/api-contract';

import { createAction } from '../actions';

import { IncompletePart } from './starting-material.types';

export const STARTING_MATERIAL_FIND_DATASETS_START = 'STARTING_MATERIAL_FIND_DATASETS_START';
export const STARTING_MATERIAL_FIND_DATASETS_FULFILL = 'STARTING_MATERIAL_FIND_DATASETS_FULFILL';
export const STARTING_MATERIAL_FIND_DATASETS_REJECT = 'STARTING_MATERIAL_FIND_DATASETS_REJECT';

export const STARTING_MATERIAL_CREATE_UPLOAD_START = 'STARTING_MATERIAL_CREATE_UPLOAD_START';
export const STARTING_MATERIAL_CREATE_UPLOAD_FULFILL = 'STARTING_MATERIAL_CREATE_UPLOAD_FULFILL';
export const STARTING_MATERIAL_CREATE_UPLOAD_REJECT = 'STARTING_MATERIAL_CREATE_UPLOAD_REJECT';

export const STARTING_MATERIAL_COMPLETE_UPLOAD_START = 'STARTING_MATERIAL_COMPLETE_UPLOAD_START';
export const STARTING_MATERIAL_COMPLETE_UPLOAD_FULFILL =
  'STARTING_MATERIAL_COMPLETE_UPLOAD_FULFILL';
export const STARTING_MATERIAL_COMPLETE_UPLOAD_REJECT = 'STARTING_MATERIAL_COMPLETE_UPLOAD_REJECT';

export const STARTING_MATERIAL_RESUME_UPLOAD_START = 'STARTING_MATERIAL_RESUME_UPLOAD_START';
export const STARTING_MATERIAL_RESUME_UPLOAD_FULFILL = 'STARTING_MATERIAL_RESUME_UPLOAD_FULFILL';
export const STARTING_MATERIAL_RESUME_UPLOAD_REJECT = 'STARTING_MATERIAL_RESUME_UPLOAD_REJECT';

export const STARTING_MATERIAL_REMOVE_DATASET_START = 'STARTING_MATERIAL_REMOVE_DATASET_START';
export const STARTING_MATERIAL_REMOVE_DATASET_FULFILL = 'STARTING_MATERIAL_REMOVE_DATASET_FULFILL';
export const STARTING_MATERIAL_REMOVE_DATASET_REJECT = 'STARTING_MATERIAL_REMOVE_DATASET_REJECT';

export const STARTING_MATERIAL_UPLOAD_PART_START = 'STARTING_MATERIAL_UPLOAD_PART_START';
export const STARTING_MATERIAL_UPLOAD_PART_FULFILL = 'STARTING_MATERIAL_UPLOAD_PART_FULFILL';
export const STARTING_MATERIAL_UPLOAD_PART_REJECT = 'STARTING_MATERIAL_UPLOAD_PART_REJECT';

export const STARTING_MATERIAL_POLL_START = 'STARTING_MATERIAL_POLL_START';
export const STARTING_MATERIAL_POLL_FULFILL = 'STARTING_MATERIAL_POLL_FULFILL';
export const STARTING_MATERIAL_POLL_STOP = 'STARTING_MATERIAL_POLL_STOP';

const baseUrl = `${window.location.origin}/internal`;

const client = initClient(apiContract, {
  baseUrl,
  baseHeaders: {},
});

function splitFileIntoBlobs(file: File, partsCount: number, partSize: number) {
  return times(partsCount, (idx: number) => {
    const start = idx * partSize;
    const end = (idx + 1) * partSize;
    const blob = idx < partsCount ? file.slice(start, end) : file.slice(start);

    return blob;
  });
}

export function findDatasets() {
  return async (dispatch: any, getState: any): Promise<void> => {
    const state = getState();
    const isInitialized = state.startingMaterial.dataset.find.initialized;
    const isFinding = state.startingMaterial.dataset.find.processing > 0;

    if (isInitialized || isFinding) {
      return;
    }

    // Starts
    dispatch(createAction(STARTING_MATERIAL_FIND_DATASETS_START));

    try {
      const resultPredefined = await client.startingMaterials.fetchPredefinedDatasets();

      if (resultPredefined.status !== 200) {
        throw Error('Request failed');
      }
      const resultPrivate = await client.startingMaterials.fetchPrivateDatasets({ query: {} });
      if (resultPrivate.status !== 200) {
        throw Error('Request failed');
      }

      const result = [...resultPredefined.body, ...resultPrivate.body.data];

      // Fulfills
      dispatch(createAction(STARTING_MATERIAL_FIND_DATASETS_FULFILL, result));
    } catch (error) {
      // Rejects
      dispatch(createAction(STARTING_MATERIAL_FIND_DATASETS_REJECT, error as Error));
    }
  };
}

export function createUpload(name: string, file: File) {
  return async (dispatch: any, getState: any): Promise<void> => {
    // Starts
    dispatch(createAction(STARTING_MATERIAL_CREATE_UPLOAD_START));

    try {
      // const result = await nest<StartingMaterial.DatasetCreateResult>('/starting-material/dataset', {
      const result = await client.startingMaterials.createDatasetUpload({
        body: {
          name,
          fileName: file.name,
          size: BigInt(file.size),
        },
      });

      if (result.status !== 201) {
        throw Error('Request failed');
      }

      // Fulfills
      dispatch(createAction(STARTING_MATERIAL_CREATE_UPLOAD_FULFILL, result.body));

      const { id, uploadId, remainingParts, partSize } = result.body;

      if (!remainingParts || !partSize) {
        throw Error('Request failed');
      }

      const blobs = splitFileIntoBlobs(file, remainingParts.length, partSize);

      remainingParts.forEach((partUrl: IncompletePart) => {
        const { url, partNumber } = partUrl;

        dispatch(
          createAction(STARTING_MATERIAL_UPLOAD_PART_START, {
            id,
            uploadId,
            partNumber,
            url,
            blob: blobs[partNumber - 1],
          })
        );
      });
    } catch (error) {
      console.error(error);
      // Rejects
      dispatch(createAction(STARTING_MATERIAL_CREATE_UPLOAD_REJECT, error as Error));
    }
  };
}

export function resumeUpload(upload: any, file: File) {
  return async (dispatch: any, getState: any): Promise<void> => {
    // Starts
    dispatch(createAction(STARTING_MATERIAL_RESUME_UPLOAD_START));

    const { id, uploadId, size } = upload;

    if (size !== file.size) {
      throw Error('Invalid file.');
    }

    try {
      const result = await client.startingMaterials.resumeDatasetUpload({
        params: { id },
        body: { uploadId },
      });
      if (result.status !== 200) {
        throw Error('Request failed');
      }

      // Fulfills
      dispatch(createAction(STARTING_MATERIAL_RESUME_UPLOAD_FULFILL, result.body));

      const { uploadedParts, remainingParts, partSize } = result.body;

      if (!uploadedParts || !remainingParts || !partSize) {
        return;
      }
      if (remainingParts.length === 0) {
        return;
      }

      const partsCount = uploadedParts.length + remainingParts.length;
      const blobs = splitFileIntoBlobs(file, partsCount, partSize);

      remainingParts.forEach((incompletePart: IncompletePart) => {
        const { url, partNumber } = incompletePart;

        dispatch(
          createAction(STARTING_MATERIAL_UPLOAD_PART_START, {
            id,
            uploadId,
            partNumber,
            url,
            blob: blobs[partNumber - 1],
          })
        );
      });
    } catch (error) {
      // Rejects
      dispatch(createAction(STARTING_MATERIAL_RESUME_UPLOAD_REJECT, error as Error));
    }
  };
}

export function removeDatasets(datasetIds: string[]) {
  return async (dispatch: any): Promise<void> => {
    const query = { id: { $in: datasetIds } };

    // Starts
    dispatch(createAction(STARTING_MATERIAL_REMOVE_DATASET_START, { query }));

    try {
      const result = await client.startingMaterials.removeDatasets({
        body: datasetIds,
      });

      // Fulfills
      dispatch(
        createAction(
          STARTING_MATERIAL_REMOVE_DATASET_FULFILL,
          datasetIds.map(id => ({ id }))
        )
      );
    } catch (error) {
      // Rejects
      dispatch(createAction(STARTING_MATERIAL_REMOVE_DATASET_REJECT, error as Error));
    }
  };
}
